import { useEffect, useRef, useState } from 'react';

export default function Contact() {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4g3o7dfv7b2bv2nghug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact-section" ref={sectionRef} className="py-24 px-6 bg-gradient-to-br from-purple-50/50 via-white to-rose-50/50">
      <div className="container mx-auto max-w-6xl">
        <div className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-rose-500 bg-clip-text text-transparent" style={{ fontFamily: '"Playfair Display", serif' }}>
              Get In Touch
            </h2>
            <p className="text-xl text-gray-600" style={{ fontFamily: '"Lora", serif' }}>
              Let's start a conversation
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-rose-500 mx-auto rounded-full mt-6"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Email</h3>
                    <a href="mailto:clorentt@gmail.com" className="text-gray-600 hover:text-purple-600 transition-colors duration-300 cursor-pointer">
                      clorentt@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-rose-600 flex-shrink-0">
                    <i className="ri-user-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Social Media</h3>
                    <p className="text-gray-600">@clorentt</p>
                  </div>
                </div>

                <div className="pt-8">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Connect With Me</h3>
                  <div className="flex space-x-4">
                    <a href="#" className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-purple-600 text-white hover:scale-110 transition-transform duration-300 cursor-pointer">
                      <i className="ri-instagram-line text-xl"></i>
                    </a>
                    <a href="#" className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-rose-600 text-white hover:scale-110 transition-transform duration-300 cursor-pointer">
                      <i className="ri-twitter-x-line text-xl"></i>
                    </a>
                    <a href="#" className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-rose-500 text-white hover:scale-110 transition-transform duration-300 cursor-pointer">
                      <i className="ri-linkedin-fill text-xl"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <form id="contact-form" onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-300 outline-none text-sm"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-300 outline-none text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-300 outline-none text-sm"
                    placeholder="What's this about?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message <span className="text-gray-500 font-normal">(max 500 characters)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-300 outline-none resize-none text-sm"
                    placeholder="Your message..."
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    Please check all fields and try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-rose-500 text-white font-semibold rounded-xl hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="container mx-auto max-w-6xl mt-20 pt-12 border-t border-gray-200">
        <div className="text-center">
          <p className="text-gray-600 mb-4" style={{ fontFamily: '"Lora", serif' }}>
            © 2025 Clorentia Sherly. All rights reserved.
          </p>
          <a 
            href="https://readdy.ai/?origin=logo" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-purple-600 hover:text-rose-500 transition-colors duration-300 cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </div>
    </section>
  );
}
